import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { useLang, useT, useLp } from "../lib/i18n";
import { captureLeadMagnet } from "../lib/api";
import { executeRecaptcha } from "../lib/recaptcha";

type SubmitState = "idle" | "submitting" | "success" | "error";

/**
 * Team-plan waitlist landing. Linked from launch responses when commenters
 * ask "I'd buy this for my team — sign me up". Reuses the existing
 * `captureLeadMagnet` lead-capture
 * pipeline so we don't add a new backend endpoint just for v1: only the
 * email + a `teams-waitlist:size=…` source tag travel to the API today.
 * The name + notes inputs are collected in the form UX (so the user feels
 * heard) but stay client-side until a follow-up PR adds a richer schema
 * server-side.
 */
export function TeamsWaitlist() {
  const t = useT();
  const lang = useLang();
  const lp = useLp();
  const copy = t.teamsWaitlist;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [teamSize, setTeamSize] = useState<string>(copy.teamSizeOptions[0].value);
  const [notes, setNotes] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [error, setError] = useState<string | null>(null);

  const submitting = submitState === "submitting";
  const submitted = submitState === "success";

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const normalizedEmail = email.trim();
    if (!normalizedEmail.includes("@")) {
      setSubmitState("error");
      setError(copy.invalidEmail);
      return;
    }

    setSubmitState("submitting");
    setError(null);
    try {
      const recaptchaToken = await executeRecaptcha("teams_waitlist");
      // Encode the team size into the `source` tag so it lands in the
      // marketing CRM alongside the email without needing a richer
      // backend schema today. Name + notes stay client-side until we
      // ship the follow-up endpoint that accepts structured fields.
      await captureLeadMagnet({
        email: normalizedEmail,
        locale: lang,
        source: `teams_waitlist:size=${teamSize}`,
        leadMagnet: "teams-waitlist",
        recaptchaToken,
        website,
        referrer: document.referrer || undefined,
      });
      setSubmitState("success");
    } catch {
      setSubmitState("error");
      setError(copy.error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title={copy.seoTitle}
        description={copy.seoDescription}
        noindex
      />
      <Header />

      <main className="flex-1">
        <section className="border-b border-margin dark:border-paper/10">
          <div className="mx-auto max-w-3xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
            <p className="text-xs font-medium uppercase tracking-wider text-ink/55 dark:text-paper/55">
              {copy.eyebrow}
            </p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink dark:text-paper">
              {copy.heading}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink/75 dark:text-paper/75">
              {copy.subheading}
            </p>
          </div>
        </section>

        <section>
          <div className="mx-auto max-w-2xl px-6 py-16">
            {submitted ? (
              <div className="rounded-2xl border border-ink/10 bg-paper p-8 dark:bg-ink dark:border-paper/12">
                <h2 className="font-display text-2xl font-semibold text-ink dark:text-paper">
                  {copy.afterSubmit.heading}
                </h2>
                <p className="mt-4 text-ink/75 dark:text-paper/75 leading-relaxed">
                  {copy.afterSubmit.body}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to={lp + "/"}
                    className="inline-flex items-center rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
                  >
                    {copy.afterSubmit.ctaHome}
                  </Link>
                  <Link
                    to={lp + "/pricing"}
                    className="inline-flex items-center rounded-full border border-ink/15 px-5 py-2.5 text-sm font-medium text-ink hover:bg-ink/5 transition-colors dark:border-paper/20 dark:text-paper dark:hover:bg-paper/5"
                  >
                    {copy.afterSubmit.ctaPricing}
                  </Link>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="rounded-2xl border border-ink/10 bg-paper p-6 sm:p-8 dark:bg-ink dark:border-paper/12 space-y-5"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block text-xs uppercase tracking-[0.12em] text-ink/60 dark:text-paper/60">
                      {copy.nameLabel}
                    </span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={copy.namePlaceholder}
                      autoComplete="name"
                      className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink/45 focus:border-ink/30 focus:outline-none dark:border-paper/20 dark:bg-ink/60 dark:text-paper dark:placeholder:text-paper/45"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-xs uppercase tracking-[0.12em] text-ink/60 dark:text-paper/60">
                      {copy.emailLabel}
                    </span>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={copy.emailPlaceholder}
                      autoComplete="email"
                      className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink/45 focus:border-ink/30 focus:outline-none dark:border-paper/20 dark:bg-ink/60 dark:text-paper dark:placeholder:text-paper/45"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="mb-2 block text-xs uppercase tracking-[0.12em] text-ink/60 dark:text-paper/60">
                    {copy.teamSizeLabel}
                  </span>
                  <select
                    required
                    value={teamSize}
                    onChange={(e) => setTeamSize(e.target.value)}
                    className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink focus:border-ink/30 focus:outline-none dark:border-paper/20 dark:bg-ink/60 dark:text-paper"
                  >
                    {copy.teamSizeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-2 block text-xs uppercase tracking-[0.12em] text-ink/60 dark:text-paper/60">
                    {copy.notesLabel}
                  </span>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={copy.notesPlaceholder}
                    className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink/45 focus:border-ink/30 focus:outline-none dark:border-paper/20 dark:bg-ink/60 dark:text-paper dark:placeholder:text-paper/45 resize-y"
                  />
                </label>

                {/* Honeypot — hidden field bots fill in but humans don't.
                    Server-side discards any submission with this populated. */}
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />

                {error && (
                  <p className="text-sm text-rec">{error}</p>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors disabled:opacity-60 disabled:cursor-progress dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
                  >
                    {submitting ? copy.ctaLoading : copy.cta}
                  </button>
                  <p className="text-xs text-ink/55 dark:text-paper/55 leading-relaxed sm:text-right max-w-md">
                    {copy.privacyNote}
                  </p>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
